import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { BsImage } from "react-icons/bs";
import Handler from "../../Helpers/Handler";
import { textCapitalize } from "../../Helpers/Utils";

const BrandFormModal = ({ mode = "create", brand, onClose, onSaved }) => {
    const modalRef = useRef(null);
    const bsModalRef = useRef(null);
    const logoFileRef = useRef(null);
    const [name, setName] = useState(brand?.brand || "");
    const [brandType, setBrandType] = useState(brand?.brandType || "cars");
    const [logoPreview, setLogoPreview] = useState(brand?.logo || "");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const el = modalRef.current;
        if (!el || !window.bootstrap) return undefined;
        const instance = window.bootstrap.Modal.getOrCreateInstance(el);
        bsModalRef.current = instance;
        const handleHidden = () => onClose?.();
        el.addEventListener("hidden.bs.modal", handleHidden);
        instance.show();
        return () => {
            el.removeEventListener("hidden.bs.modal", handleHidden);
            instance.hide();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleLogoFile = (e) => {
        const file = e.target.files?.[0];
        if (file) setLogoPreview(URL.createObjectURL(file));
    };

    const onSubmit = async () => {
        const trimmed = name.trim();
        if (!trimmed) {
            toast.error("Brand name is required");
            return;
        }
        const logoFile = logoFileRef.current?.files?.[0];
        if (mode === "create" && !logoFile) {
            toast.error("A logo is required");
            return;
        }

        setSaving(true);
        const fd = new FormData();
        fd.append("brand", trimmed);
        if (mode === "create") fd.append("brandType", brandType);
        if (logoFile) fd.append("logo", logoFile);

        const response = mode === "edit"
            ? await Handler({ method: "patch", url: `/staff/vehicle/brand/${brand.brandId}`, data: fd, formdata: true })
            : await Handler({ method: "post", url: "/staff/vehicle/brand", data: fd, formdata: true });

        if (response?.success) {
            toast.success(response?.data?.message || (mode === "edit" ? "Brand updated" : "Brand created"));
            onSaved?.();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to save the brand"));
        }
        setSaving(false);
    };

    return (
        <div className="modal fade" tabIndex="-1" ref={modalRef} aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content border-0 rounded-4">
                    <div className="modal-header">
                        <h5 className="modal-title fw-semibold">{mode === "edit" ? "Edit Brand" : "Add Brand"}</h5>
                        <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                        <div className="row g-3">
                            <div className="col-12 col-md-7">
                                <label className="form-label">Brand name</label>
                                <input type="text" className="form-control" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Maruti Suzuki" />
                            </div>
                            <div className="col-12 col-md-5">
                                <label className="form-label">Vehicle type</label>
                                <select className="form-select" value={brandType} onChange={(e) => setBrandType(e.target.value)} disabled={mode === "edit"}>
                                    <option value="cars">Cars</option>
                                    <option value="bikes">Bikes</option>
                                </select>
                                {mode === "edit" ? <div className="form-text">Can't be changed after creation.</div> : null}
                            </div>

                            <div className="col-12">
                                <label className="form-label d-block">Logo{mode === "create" ? "" : "(optional - keeps current if left blank)"}</label>
                                <div className="settings-image-uploader">
                                    <div className="settings-image-preview">
                                        {logoPreview ? <img src={logoPreview} alt="Logo preview" /> : <BsImage />}
                                    </div>
                                    <input ref={logoFileRef} type="file" accept="image/*" className="form-control" onChange={handleLogoFile} />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-outline-secondary" data-bs-dismiss="modal" disabled={saving}>Cancel</button>
                        <button type="button" className="btn btn-primary" onClick={onSubmit} disabled={saving}>
                            {saving ? "Saving..." : mode === "edit" ? "Save changes" : "Create brand"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BrandFormModal;
